import React from 'react';
import { Box, Container, Typography, Grid, Paper, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import BuildIcon from '@mui/icons-material/Build';

const Skills = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const skillCategories = [
    {
      icon: <CodeIcon sx={{ fontSize: 40 }} />,
      title: t('skills.frontend.title'),
      skills: t('skills.frontend.items', { returnObjects: true })
    },
    {
      icon: <StorageIcon sx={{ fontSize: 40 }} />,
      title: t('skills.backend.title'),
      skills: t('skills.backend.items', { returnObjects: true })
    },
    {
      icon: <BuildIcon sx={{ fontSize: 40 }} />,
      title: t('skills.tools.title'),
      skills: t('skills.tools.items', { returnObjects: true })
    }
  ];

  return (
    <Box
      id="skills"
      sx={{
        py: 8,
        background: 'linear-gradient(180deg, #0a1428 0%, #050810 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants}>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 2,
                fontWeight: 700,
                background: 'linear-gradient(45deg, #ffffff 30%, #22e165 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('skills.title')}
            </Typography>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Typography
              variant="h5"
              sx={{
                textAlign: 'center',
                mb: 6,
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 500
              }}
            >
              {t('skills.subtitle')}
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {skillCategories.map((category, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div variants={itemVariants}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      background: 'rgba(34, 225, 101, 0.05)',
                      border: '1px solid rgba(34, 225, 101, 0.1)',
                      borderRadius: 4,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(34, 225, 101, 0.1)',
                        borderColor: 'rgba(34, 225, 101, 0.2)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          color: theme.palette.primary.main,
                          mr: 2
                        }}
                      >
                        {category.icon}
                      </Box>
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                        {category.title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {category.skills.map((skill, skillIndex) => (
                        <Box
                          key={skillIndex}
                          sx={{
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            background: 'rgba(34, 225, 101, 0.1)',
                            border: '1px solid rgba(34, 225, 101, 0.2)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'rgba(34, 225, 101, 0.2)',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'white',
                              fontWeight: 500
                            }}
                          >
                            {skill}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Skills; 